import { Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { InfoCard2 } from '../../../../widgets/components/UI/InfoCard2'
import { Formik, Form } from 'formik'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import TextInput from '../../../../widgets/components/Input/TextInput'
import { SelectInput } from '../../../../widgets/components/Input/SelectInput'

import deleteicon from '../../../../../../media/stakeholders/ic-delete.svg'
import { ToolTipUIForModal } from '../../../../widgets/components/UI/ToolTipUIForModal'
import * as Yup from 'yup'
import { useCurrency } from '../../../../../hooks/useCurrency'
import { useAuth } from '../../../../auth'
import { useGetAllSecurityClassesDropdown } from '../../../../../hooks/useGetAllSecurityClassesDropdown'
import { SecurityClassDropdown } from '../../../components/security-class-dropdown'
import { FileUpload } from '../../../../widgets/components/FileUpload'
import Skeleton from 'react-loading-skeleton'
import { getFileType } from '../../../../../core/_util'
import {
  createWarrantSecurityClass,
  getWarrantSecurityClassById,
  updateWarrantSecurityClass,
} from '../../core/_requests'
import { toast } from 'react-toastify'
import { allowedFileTypes } from '../../../../../core/_constants'
import { DeleteFileConfirmationModal } from '../../../components/delete-file-confirmation-modal'
import { TransactionModalType } from '../../core/_models'
import { useStakeholders } from '../../../Stakeholders'
import { TransactionModals } from '../../core/_constants'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../../hooks/gtmHelpers'
import brandingConfig from '../../../../../../config/brandingConfig'

export default function AddWarrantClassModal({
  isActive,
  setIsActive,
  warrantClassId,
  setIsWarrantCreateOrUpdate,
  setSidebar,
  openModal,
  setIsShareClassDropdown,
  setModalType,
  setIsCreate,
  modalType,
  warrantClassDropdown,
  setWarrantClassDropdown,
}: {
  isActive: boolean
  setIsActive: () => void
  warrantClassId?: string | null
  setIsWarrantCreateOrUpdate: Dispatch<SetStateAction<boolean>>
  setSidebar: Dispatch<SetStateAction<number>>
  openModal: (modalType: TransactionModalType) => void
  setIsShareClassDropdown: Dispatch<SetStateAction<boolean>>
  setModalType: Dispatch<SetStateAction<TransactionModalType>>
  setIsCreate: Dispatch<SetStateAction<boolean>>
  modalType?: TransactionModalType
  warrantClassDropdown?: boolean
  setWarrantClassDropdown?: Dispatch<SetStateAction<boolean>>
}) {
  const { formatMessage } = useIntl()
  const [currentDocumentStep, setCurrentDocumentStep] = useState(1)
  const formikRef = useRef<any>(null)
  const { currencyOptions } = useCurrency()
  const { companyId, companyCurrency, currentUser } = useAuth()
  const { shareSecuirtyClassDropdownData, shareSecuirtyDropdownDataLoading } =
    useGetAllSecurityClassesDropdown(TransactionModals.ADD_WARRANT_CLASS_MODAL)
  const [modelStatus, setModelStatus] = useState(false)
  const [warrantClassDetailsLoading, setWarrantClassDetailsLoading] = useState(false)
  const [isNotInitialized, setIsNotInitiaized] = useState(true)
  const [warrantSecurityClassDetails, setWarrantSecurityClassDetails] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const fileRef = useRef<any>([])
  const [deleteFileId, setDeleteFileId] = useState<any>(null)
  const [showDeleteModal, setDeleteShowModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const {
    createdShareClassFromOtherClasses,
    createdShareClassEditWarrantClassId,
    setCreatedWarrantClassFromOtherTransaction,
  } = useStakeholders()

  const initialValues = {
    className: warrantSecurityClassDetails?.className || null,
    tagColor: warrantSecurityClassDetails?.tagColor || '#F56767',
    classCode: warrantSecurityClassDetails?.classCode || null,
    currency:
      currencyOptions.find((currencyId) => currencyId?.name?.split(' ')[0] === companyCurrency)
        ?.value ?? null,
    boardApprovalDate: warrantSecurityClassDetails?.boardApprovalDate || null,
    expirationPeriod: warrantSecurityClassDetails?.expirationPeriod || null,
    shareClassId: createdShareClassFromOtherClasses
      ? shareSecuirtyClassDropdownData.find(
        (shareSecurityClass) => shareSecurityClass.id === createdShareClassFromOtherClasses
      )
      : warrantSecurityClassDetails?.shareClassId
        ? shareSecuirtyClassDropdownData.find(
          (shareSecurityClass) =>
            shareSecurityClass.id === warrantSecurityClassDetails?.shareClassId
        )
        : null,
    exercisePrice: warrantSecurityClassDetails?.exercisePrice || null,
    fileIds: warrantSecurityClassDetails?.documents?.map((document: any) => document.fileId) || [],
  }

  const validationSchema = Yup.object().shape({
    className: Yup.string()
      .nullable()
      .required(formatMessage({ id: 'Warrant Class Name is required' }))
      .max(30, formatMessage({ id: 'Warrant Class Name must not exceed 30 characters' })),

    tagColor: Yup.string().required(formatMessage({ id: 'Tag Color is required' })),
    classCode: Yup.string()
      .nullable()
      .required(formatMessage({ id: 'Class Code is required' })),
    boardApprovalDate: Yup.string()
      .nullable()
      .required(formatMessage({ id: 'Board Approval Date is required' })),
    expirationPeriod: Yup.number()
      .required(formatMessage({ id: 'Expiration Period is required' }))
      .min(0, formatMessage({ id: 'Expiration Period must be positive number' }))
      .test('max-length', 'Expiration Period  must not exceed 20 characters', (value) =>
        value ? value.toString().length <= 20 : true
      ),
    shareClassId: Yup.object().required(formatMessage({ id: 'Share Class is required' })),
    exercisePrice: Yup.number()
      .required(formatMessage({ id: 'Exercise Price is required' }))
      .min(0, formatMessage({ id: 'Exercise Price must be positive number' }))
      .test('max-length', 'Exercise Price  must not exceed 20 characters', (value) =>
        value ? value.toString().length <= 20 : true
      ),
  })

  const handleSubmit = async (values: any, { resetForm }: any) => {
    const rawPayload = {
      className: values?.className,
      tagColor: values?.tagColor,
      classCode: values?.classCode,
      currency: values?.currency?.value || values?.currency?.id,
      boardApprovalDate: values?.boardApprovalDate,
      expirationPeriod: parseInt(values?.expirationPeriod),
      shareClassId: values?.shareClassId?.value || values?.shareClassId?.id,
      exercisePrice: parseInt(values?.exercisePrice),
      fileIds: values?.fileIds,
    }

    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(
        ([_, value]) =>
          value !== null &&
          value !== undefined &&
          value !== '' &&
          (!isNaN(value) || typeof value === 'string' || Array.isArray(value))
      )
    )

    try {
      if (!companyId) {
        return
      }
      setIsLoading(true)
      if (
        (warrantClassId && warrantSecurityClassDetails) ||
        (createdShareClassEditWarrantClassId && warrantSecurityClassDetails)
      ) {
        const {
          data: { data, success, errors },
        } = await updateWarrantSecurityClass(
          payload,
          companyId,
          createdShareClassEditWarrantClassId
            ? createdShareClassEditWarrantClassId
            : warrantClassId!
        )

        if (success) {
          resetForm()
          setFiles([])
          setWarrantSecurityClassDetails(null)
          setIsWarrantCreateOrUpdate(true)
          if (warrantClassDropdown) {
            if (setWarrantClassDropdown) {
              if (openModal) {
                if (modalType === 'AddWarrantTransaction') {
                  setCreatedWarrantClassFromOtherTransaction(data.warrantClassId)
                  openModal('AddWarrantTransaction')
                }
                if (setModalType) {
                  setModalType('none')
                }
              }
              setWarrantClassDropdown(false)
              setSidebar(3)
            }
          } else {
            setIsActive()
            setSidebar(3)
          }
          setIsLoading(false)
          toast.success(formatMessage({ id: 'Warrant security class updated successfully' }))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateWarrantSecurityClassSuccessful,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                warrant_security_class_details: data,
              },
            },
          })
        } else {
          toast.error(errors[0])
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateWarrantSecurityClassFailed,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                warrant_security_class_details: payload,
                error: errors[0],
              },
            },
          })
        }
      } else {
        const {
          data: { data, success, errors },
        } = await createWarrantSecurityClass(payload, companyId)

        if (success) {
          toast.success(formatMessage({ id: 'Warrant security class created successfully' }))
          resetForm()
          setFiles([])
          setWarrantSecurityClassDetails(null)
          setIsLoading(false)
          setSidebar(3)
          setIsWarrantCreateOrUpdate(true)

          if (warrantClassDropdown) {
            if (setWarrantClassDropdown) {
              if (openModal) {
                if (modalType === 'AddWarrantTransaction') {
                  setCreatedWarrantClassFromOtherTransaction(data.warrantClassId)
                  openModal('AddWarrantTransaction')
                }
                if (setModalType) {
                  setModalType('none')
                }
              }
              setWarrantClassDropdown(false)
              setSidebar(3)
            }
          } else {
            setIsActive()
            setSidebar(3)
          }
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.AddWarrantSecurityClassSuccessful,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                warrant_security_class_details: payload,
              },
            },
          })
        } else {
          toast.error(errors[0])
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.AddWarrantSecurityClassFailed,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                warrant_security_class_details: payload,
                error: errors[0],
              },
            },
          })
        }
      }
    } catch (err) {
      console.error('Error:', err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.AddWarrantSecurityClassFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            warrant_security_class_details: payload,
            error: err,
          },
        },
      })
    } finally {
      setIsLoading(false)
    }
  }

  const nextStep = () => {
    if (currentDocumentStep < 3) {
      setCurrentDocumentStep(currentDocumentStep + 1)
    }
  }
  const prevStep = () => {
    if (currentDocumentStep > 1) {
      setCurrentDocumentStep(currentDocumentStep - 1)
    }
  }

  const [files, setFiles] = useState<any>([])

  const fetchConvertibleSecurityClassDetails = async () => {
    if (!(warrantClassId || createdShareClassEditWarrantClassId)) return
    setWarrantClassDetailsLoading(true)
    try {
      const {
        data: { success, data },
      } = await getWarrantSecurityClassById(
        companyId!,
        createdShareClassEditWarrantClassId ? createdShareClassEditWarrantClassId : warrantClassId!
      )
      if (success && data) {
        setWarrantSecurityClassDetails(data)
        setSelectedColor(data?.tagColor)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setWarrantClassDetailsLoading(false)
    }
  }

  useEffect(() => {
    fetchConvertibleSecurityClassDetails()
  }, [warrantClassId, createdShareClassEditWarrantClassId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (warrantSecurityClassDetails?.documents.length > 0 && isNotInitialized) {
      setFiles(
        warrantSecurityClassDetails?.documents.map(
          ({ fileId, name }: { fileId: string; name: string }) => ({
            id: fileId,
            name,
          })
        )
      )
    }
  }, [isNotInitialized, warrantSecurityClassDetails?.documents]) // eslint-disable-line react-hooks/exhaustive-deps

  const deleteFile = () => {
    if (!deleteFileId) return
    setDeleteLoading(true)
    setFiles(files.filter((file: any) => file.id !== deleteFileId))
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'fileIds',
        files.filter((file: any) => file.id !== deleteFileId).map((file: any) => file.id)
      )
    }
    toast.success('Document deleted successfully')
    setDeleteLoading(false)
    setDeleteShowModal(false)
  }

  // select colour

  const [selectedColor, setSelectedColor] = useState('#F56767')
  const colors = [
    { color: '#F56767', id: 'red' },
    { color: '#AA72E8', id: 'purple' },
    { color: '#B30B30', id: 'darkRed' },
    { color: '#FF66FF', id: 'pink' },
    { color: '#66CC66', id: 'green' },
    { color: '#FFD700', id: 'yellow' },
    { color: '#3399FF', id: 'blue' },
  ]

  const handleColorClick = (color: any) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('tagColor', color)
    }
    setSelectedColor(color)
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        enableReinitialize
        validateOnMount
      >
        {(formik) => (
          <Form>
            <Modal show={isActive} onHide={() => setIsActive()} size='lg' fullscreen>
              <Modal.Header closeButton>
                <h2>
                  {formatMessage({ id: warrantClassId ? 'Edit Warrant Class' : 'Add Warrant Class' })}
                </h2>
              </Modal.Header>
              <Modal.Body className='container'>
                <div
                  className='stepper stepper-pills stepper-column d-flex flex-column'
                  id='kt_create_account_stepper'
                >
                  <div className='d-flex justify-content-start flex-column w-100'>
                    <div className='card-body px-0 d-flex justify-content-start'>
                      <div
                        className='d-flex gap-6 align-items-center'
                        style={{
                          overflowX: 'auto',
                          whiteSpace: 'nowrap',
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                        }}
                      >
                        {[1, 2].map((step) => (
                          <div
                            key={step}
                            className={`stepper-item ${currentDocumentStep === step ? 'current' : ''
                              }
                                custom-marign-right`}
                            style={{ display: 'inline-block' }}
                          >
                            <div className='stepper-wrapper'>
                              <div className='stepper-icon w-40px h-40px'>
                                <i className='stepper-check'></i>
                                <span className='stepper-number'>{step}</span>
                              </div>
                              <div className='stepper-label'>
                                <h3 className='stepper-title fw-semibold font-size-13'>
                                  {formatMessage({
                                    id: step === 1 ? 'Step 1' : 'Step 2',
                                  })}
                                </h3>
                                <div className='stepper-desc font-size-12'>
                                  {formatMessage({
                                    id: step === 1 ? 'Basic Details' : 'Documents',
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className='d-flex flex-column justify-content-between'>
                    <div className=''>
                      {/* Step 1 */}
                      {currentDocumentStep === 1 && (
                        <div className='w-100'>
                          <InfoCard2
                            title='How to Add Warrant Class?'
                            desc='Easily add Warrant Class details, including class name, prefix, exchange rate, and relevant documents. These details are essential for maintaining a well-defined relationship with your shareholders and investors.'
                            slug={brandingConfig.helpLinks.addWarrantClass}
                            className='w-100 mb-0'
                          />

                          <div className='mb-4'>
                            {warrantClassDetailsLoading ? (
                              <div className='mt-md-8 w-100 px-5 px-md-0 mb-4'>
                                <div className='d-md-flex col-md-12 flex-wrap gap-6 mb-10'>
                                  <div className='col-md-5 mb-10 mb-md-0'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                  <div className='col-md-5'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className='d-md-flex col-md-12 flex-wrap gap-6 mb-10'>
                                  <div className='col-md-5 mb-10 mb-md-0'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                  <div className='col-md-5'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className='d-md-flex col-md-12 flex-wrap gap-6 mb-10'>
                                  <div className='col-md-5 mb-10 mb-md-0'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                  <div className='col-md-5'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className='d-md-flex col-md-12 flex-wrap gap-6 mb-10'>
                                  <div className='col-md-5 mb-10 mb-md-0'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                  <div className='col-md-5'>
                                    <div className='w-50'>
                                      <Skeleton
                                        height={20}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                    <div className='mt-2'>
                                      <Skeleton
                                        height={30}
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className='w-100 mt-10'>
                                <div className='row'>
                                  <div className='col-md-6 col-12'>
                                    <SecurityClassDropdown
                                      buttonLabel={formatMessage({ id: 'Add New Share Class' })} // Button label to add new share class
                                      label={formatMessage({ id: 'Share Class' })} // Label for the dropdown
                                      fieldName='shareClassId' // Updated to reflect a proper field name matching formik's initial values
                                      toolTipText={formatMessage({
                                        id: 'Select class name of share security class', // Tooltip for better clarity
                                      })}
                                      loading={shareSecuirtyDropdownDataLoading}
                                      options={shareSecuirtyClassDropdownData} // Pass the options correctly
                                      formik={formik} // Pass the Formik instance
                                      placeholder={formatMessage({ id: 'Select Share Class' })} // Placeholder text for the input
                                      ariaLabel='className' // Aria-label for accessibility
                                      isTooltipForModal={true} // Tooltip for modal
                                      isPopulateData={true} // Populate data
                                      onButtonClick={() => {
                                        setIsCreate(true)
                                        setModalType('AddWarrantClassModal')

                                        setIsShareClassDropdown(true)

                                        openModal('AddSecurityClassModal')
                                      }}
                                    />
                                  </div>
                                  <div className='col-md-6 col-12'>
                                    <TextInput
                                      fieldType='text'
                                      label={formatMessage({ id: 'Warrant Class Name' })}
                                      fieldName='className'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter Warrant Class Name',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Add a name to the Warrant Class that you are creating.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                  <div className='col-md-6 col-12'>
                                    <TextInput
                                      fieldType='text'
                                      label={formatMessage({ id: 'Warrant Class Prefix' })}
                                      fieldName='classCode'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter Warrant Class Prefix',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Add a prefix to the Warrant Class that you are creating.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                  <div className='col-md-6 col-12'>
                                    <TextInput
                                      fieldType='date'
                                      label={formatMessage({ id: 'Board Approval Date' })}
                                      fieldName='boardApprovalDate'
                                      isDateOfBirth={true}
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter Board Approval Date',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Specify the date of board approval for this Warrant Class.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                      isLesserThanShareClassFirstClosingDate={true}
                                    />
                                  </div>
                                  <div className='col-md-6 col-12'>
                                    <SelectInput
                                      label={formatMessage({ id: 'Currency' })}
                                      fieldName='currency'
                                      placeholder={formatMessage({
                                        id: 'Select Currency',
                                      })}
                                      formik={formik}
                                      toolTipText={formatMessage({
                                        id: 'Select the currency for this Warrant Class.',
                                      })}
                                      options={currencyOptions}
                                      isStarRequired={false}
                                      isTooltipForModal={true}
                                      disabled={true}
                                    />
                                  </div>
                                  <div className='col-md-6 col-12 mb-8'>
                                    <div className='d-flex'>
                                      <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                                        {formatMessage({ id: 'Select Tag Color *' })}
                                      </label>
                                      <ToolTipUIForModal tooltipText='select tag color for warrant issuance' />
                                    </div>
                                    <div className='d-flex align-items-center'>
                                      {colors.map((colorObj) => (
                                        <div
                                          key={colorObj.id}
                                          onClick={() => handleColorClick(colorObj.color)}
                                          className='me-2 rounded-circle mt-1'
                                          style={{
                                            backgroundColor: colorObj.color,
                                            width: '30px',
                                            height: '30px',
                                            border:
                                              selectedColor === colorObj.color
                                                ? '3px solid black'
                                                : 'none',
                                            cursor: 'pointer',
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  <div className='col-md-6 col-12'>
                                    <TextInput
                                      fieldType='number'
                                      label={formatMessage({ id: 'Expiration Period (in years)' })}
                                      fieldName='expirationPeriod'
                                      formik={formik}
                                      placeholder={formatMessage({ id: 'Enter Expiration Period' })}
                                      toolTipText={formatMessage({
                                        id: 'Specify the expiration period (in number of years) for this Warrant Class.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                  <div className='col-md-6 col-12'>
                                    <TextInput
                                      fieldType='number'
                                      label={formatMessage({ id: 'Exercise Price' })}
                                      fieldName='exercisePrice'
                                      formik={formik}
                                      placeholder={formatMessage({ id: 'Enter Exercise Price' })}
                                      toolTipText={formatMessage({
                                        id: 'Add an exercise price for this Warrant Class.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Step 2 */}
                      {currentDocumentStep === 2 && (
                        <div className='w-100'>
                          <InfoCard2
                            title='How to Add Warrant Class?  '
                            desc='Easily add Warrant Class details, including class name, prefix, exchange rate, and relevant documents. These details are essential for maintaining a well-defined relationship with your shareholders and investors.'
                            slug={brandingConfig.helpLinks.addWarrantClass}
                            className='w-100 mb-0'
                          />

                          <div className=''>
                            <div className='d-inline-block form-label  text-dark font-size-13 mt-4'>
                              <div className='d-flex'>
                                <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                                  {formatMessage({ id: 'Attach files' })}
                                </label>
                                <ToolTipUIForModal tooltipText='Add/upload documents pertaining to this Warrant Class like Sale of Warrants Agreement, etc.' />
                              </div>
                            </div>
                            <div className='w-100'>
                              <button
                                className='btn btn-primary border border-1 mb-2 px-6 py-4 text-center w-100 w-md-auto'
                                onClick={() => setModelStatus(true)}
                              >
                                {formatMessage({ id: 'Import Files' })}
                              </button>
                            </div>
                          </div>

                          <div className='mb-4'>
                            {files.length > 0 && (
                              <div className='d-flex form-label  text-dark font-size-13 mt-4'>
                                {formatMessage({ id: 'Uploaded Files' })}{' '}
                                <ToolTipUIForModal
                                  tooltipText={formatMessage({
                                    id: 'Manage all the uploaded documents here.',
                                  })}
                                />
                              </div>
                            )}
                            <FileUpload
                              fileSize={parseInt(
                                process.env.REACT_APP_STAKEHOLDERS_MAX_FILE_SIZE as string
                              )}
                              maxFileNumber={10}
                              allowType={allowedFileTypes}
                              metaData={{ module: 'document', isProtected: false }}
                              modalStatus={modelStatus}
                              handleClose={() => {
                                TagManager.dataLayer({
                                  dataLayer: {
                                    event:
                                      GTMEvent.WarrantSecurityClassCreateOrUpdateDocumentsUploadFailed,
                                    action: 'normal',
                                    metadata: {
                                      company_id: companyId,
                                      user_id: currentUser?.userId,
                                      email: currentUser?.email,
                                    },
                                  },
                                })
                                setModelStatus(false)
                              }}
                              handleSuccess={(id: number, name: string) => {
                                setIsNotInitiaized(false)

                                setFiles((prevFiles: any) => [...prevFiles, { id, name }])

                                if (formikRef.current) {
                                  const currentFileIds = Array.isArray(
                                    formikRef.current.values.fileIds
                                  )
                                    ? formikRef.current.values.fileIds
                                    : []

                                  fileRef.current.push(id) // Store only the current ID
                                  const uniqueFileIds = Array.from(
                                    new Set([...currentFileIds, ...fileRef.current])
                                  )
                                  formikRef.current.setFieldValue('fileIds', uniqueFileIds)
                                  TagManager.dataLayer({
                                    dataLayer: {
                                      event:
                                        GTMEvent.WarrantSecurityClassCreateOrUpdateDocumentsUploadSuccessful,
                                      action: 'normal',
                                      metadata: {
                                        company_id: companyId,
                                        user_id: currentUser?.userId,
                                        email: currentUser?.email,
                                        document_name: name,
                                        document_id: id,
                                      },
                                    },
                                  })
                                }
                              }}
                              resourceType='all'
                            />
                            {warrantClassDetailsLoading ? (
                              <>
                                <div className='w-50'>
                                  <Skeleton
                                    width={'100%'}
                                    height={48}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='w-50'>
                                  <Skeleton
                                    width={'100%'}
                                    height={48}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </>
                            ) : (
                              <div className='w-100'>
                                <ul className='p-0 m-0'>
                                  {files?.map((file: any) => (
                                    <li
                                      key={file.id}
                                      className='d-flex align-items-center gap-2 text-decoration-none mb-3 w-100 border border-1 p-2'
                                    >
                                      <div className='d-flex justify-content-between align-items-center w-100'>
                                        <div className='w-100 d-flex align-items-center gap-2'>
                                          <img
                                            src={getFileType(file.name)}
                                            alt={file.name}
                                            style={{ height: '30px', width: '30px' }}
                                          />

                                          <p className='p-0 m-0 max-file-width'>
                                            {file.name.split('/').pop()}
                                          </p>
                                        </div>

                                        <button
                                          type='button'
                                          className='btn btn-secondry p-0 m-0  d-flex align-items-start'
                                          onClick={() => {
                                            setDeleteFileId(file.id)
                                            setDeleteShowModal(true)
                                          }}
                                        >
                                          <img src={deleteicon} alt='Delete' />
                                        </button>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className='container px-0'>
                <div className='d-flex flex-md-row flex-column justify-content-md-end justify-content-center gap-3 w-100'>
                  {currentDocumentStep > 1 ? (
                    <div>
                      <button type='button' className='btn btn-light text-muted w-100' onClick={prevStep}>
                        {formatMessage({ id: 'Back' })}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <button
                        type='button'
                        className='btn bg-light custom-button-property text-muted w-100'
                        onClick={() => setIsActive()}
                      >
                        {formatMessage({ id: 'Cancel' })}
                      </button>
                    </div>
                  )}
                  <div>
                    {currentDocumentStep < 2 ? (
                      <button
                        type='button'
                        className='btn bg-primary custom-button-property text-white w-100'
                        disabled={!formik.isValid}
                        onClick={nextStep}
                      >
                        {formatMessage({ id: 'Next' })}
                      </button>
                    ) : (
                      <button
                        type='submit'
                        className='btn btn-primary w-100'
                        disabled={!formik.isValid || isLoading}
                        onClick={formik.submitForm}
                      >
                        {!isLoading && (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Submit' })}
                          </span>
                        )}
                        {isLoading && (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )}
      </Formik>
      <DeleteFileConfirmationModal
        setDeleteShowModal={setDeleteShowModal}
        showDeleteModal={showDeleteModal}
        onDeleteSubmit={deleteFile}
        deleteLoading={deleteLoading}
        header='Delete File'
        paragraph='Are you sure you want to delete this file?'
      />
    </>
  )
}
